import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectAuth } from "../../../../redux/features/auth/auth-slice";
import {
  selectIsOnCancelPressed,
  selectIsOnConfirmPressed,
  setIsOnCancelPressed,
  setIsOnConfirmPressed,
  setIsShowEstimationInterruptModal,
} from "../../../../redux/features/solar-estimation/solarEstimationSlice";
import { AppRoute } from "../../../../shared/oversight-core/interfaces/app-routes";
import ArrowButton from "../../../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import StepsNumber, {
  ISteps,
} from "../../../../shared/oversight-core/ui-elements/steps-number/steps-number";
import { isOnEstimation } from "../../../../shared/oversight-core/utils/solarEstimationHelper";
import styles from "./steps-header.module.scss";

interface IProps {
  steps: ISteps[];
  selectedStep: number;
  onStepClick: (stepNumber: number) => void;
  onSave: () => void;
  isApproved: boolean;
  onChangeMode?: () => void;
}

const StepsHeader = (props: IProps) => {
  const { steps, selectedStep, onStepClick, onSave, isApproved, onChangeMode } =
    props;
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const location = useLocation();
  const pathname = location.pathname;
  const isDirectMode = pathname.includes("direct");
  const isSimplifiedMode = pathname.includes("simplified");
  const isAdvancedMode = pathname.includes("advanced");
  const isWaterHeaterSystem = pathname.includes("water-heater");
  const [navigateOnInterruption, setNavigateOnInterruption] = useState("");

  const isOnConfirmPressed = useSelector(selectIsOnConfirmPressed);
  const isOnCancelPressed = useSelector(selectIsOnCancelPressed);

  const navigate = useNavigate();

  useEffect(() => {
    if (isOnConfirmPressed && navigateOnInterruption) {
      dispatch(setIsShowEstimationInterruptModal(false));
      dispatch(setIsOnConfirmPressed(false));
      setNavigateOnInterruption("");
      navigate(navigateOnInterruption);
    }
  }, [isOnConfirmPressed, navigateOnInterruption]);

  useEffect(() => {
    if (isOnCancelPressed && navigateOnInterruption) {
      setNavigateOnInterruption("");
      dispatch(setIsOnCancelPressed(false));
    }
  }, [isOnCancelPressed, navigateOnInterruption]);

  const onBack = () => {
    if (auth.token) {
      if (isWaterHeaterSystem) {
        if (pathname.includes("assemble-system")) {
          if (isOnEstimation(pathname)) {
            setNavigateOnInterruption(AppRoute.DESIGN_SYSTEM);
            dispatch(setIsShowEstimationInterruptModal(true));
            return;
          }

          navigate(AppRoute.DESIGN_SYSTEM);
          return;
        }

        if (pathname.includes("system-summary")) {
          navigate(AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM);
          return;
        }

        return;
      }

      if (isDirectMode) {
        if (pathname.includes("assemble-system")) {
          if (isOnEstimation(pathname)) {
            setNavigateOnInterruption(AppRoute.DESIGN_SYSTEM);
            dispatch(setIsShowEstimationInterruptModal(true));
            return;
          }

          navigate(AppRoute.DESIGN_SYSTEM);
          return;
        }

        if (pathname.includes("system-summary")) {
          navigate(AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM);
          return;
        }

        return;
      }

      if (isSimplifiedMode) {
        if (pathname.includes("usage-info")) {
          if (isOnEstimation(pathname)) {
            setNavigateOnInterruption(AppRoute.DESIGN_SYSTEM);
            dispatch(setIsShowEstimationInterruptModal(true));
            return;
          }

          navigate(AppRoute.DESIGN_SYSTEM);
          return;
        }

        if (pathname.includes("assemble-system")) {
          navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
          return;
        }

        if (pathname.includes("system-summary")) {
          navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM);
          return;
        }

        return;
      }

      if (isAdvancedMode) {
        if (pathname.includes("devices")) {
          if (isOnEstimation(pathname)) {
            setNavigateOnInterruption(AppRoute.DESIGN_SYSTEM);
            dispatch(setIsShowEstimationInterruptModal(true));
            return;
          }

          navigate(AppRoute.DESIGN_SYSTEM);
          return;
        }

        if (pathname.includes("usage-info")) {
          navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES);
          return;
        }

        if (pathname.includes("assemble-system")) {
          navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_USAGE_INFO);
          return;
        }

        if (pathname.includes("system-summary")) {
          navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_ASSEMBLE_SYSTEM);
          return;
        }

        return;
      }

      return;
    }

    if (isWaterHeaterSystem) {
      if (pathname.includes("assemble-system")) {
        if (isOnEstimation(pathname)) {
          setNavigateOnInterruption(AppRoute.PUBLIC_DESIGN_SYSTEM);
          dispatch(setIsShowEstimationInterruptModal(true));
          return;
        }

        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM);
        return;
      }

      if (pathname.includes("system-summary")) {
        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM);
        return;
      }

      return;
    }

    if (isDirectMode) {
      if (pathname.includes("assemble-system")) {
        if (isOnEstimation(pathname)) {
          setNavigateOnInterruption(AppRoute.PUBLIC_DESIGN_SYSTEM);
          dispatch(setIsShowEstimationInterruptModal(true));
          return;
        }

        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM);
        return;
      }

      if (pathname.includes("system-summary")) {
        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM);
        return;
      }

      return;
    }

    if (isSimplifiedMode) {
      if (pathname.includes("usage-info")) {
        if (isOnEstimation(pathname)) {
          setNavigateOnInterruption(AppRoute.PUBLIC_DESIGN_SYSTEM);
          dispatch(setIsShowEstimationInterruptModal(true));
          return;
        }

        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM);
        return;
      }

      if (pathname.includes("assemble-system")) {
        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
        return;
      }

      if (pathname.includes("system-summary")) {
        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM);
        return;
      }
    }
  };

  return (
    <div
      className={`bg-nav-bar p-4 ${styles.stepHeaderContainer} ${
        auth.token ? `rounded-3` : ``
      }`}
    >
      <Row className="align-items-center justify-content-between g-4">
        <Col className="col-auto">
          <Row className="align-items-center">
            <Col className="col-auto ps-sm-4">
              <ArrowButton onBack={onBack} />
            </Col>
            <Col>
              <Row>
                <Col className="text-label font-weight-600 font-size-20">
                  {(isWaterHeaterSystem && selectedStep === 2) ||
                  (isDirectMode && selectedStep === 2) ||
                  (isSimplifiedMode && selectedStep === 3) ||
                  selectedStep === 4
                    ? isApproved
                      ? "Approved System"
                      : "System Designed"
                    : "Design Your System"}
                </Col>
              </Row>
              {!isDirectMode && !isWaterHeaterSystem && (
                <Row>
                  <Col
                    className="text-color-mode font-weight-500 font-size-12 text-decoration-underline cursor-pointer"
                    onClick={() => onChangeMode?.()}
                  >
                    {isSimplifiedMode
                      ? "Use Advance Mode"
                      : "Use Simplified Mode"}
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Col>
        {((isWaterHeaterSystem && selectedStep !== 2) ||
          (isDirectMode && selectedStep !== 2) ||
          (isSimplifiedMode && selectedStep !== 3) ||
          (isAdvancedMode && selectedStep !== 4)) && (
          <Col
            className={`col-auto ${
              isSimplifiedMode || isDirectMode || isWaterHeaterSystem
                ? `d-none d-lg-block`
                : `d-none d-xl-block`
            }`}
          >
            <StepsNumber
              steps={steps}
              selectedStep={selectedStep}
              onStepClick={(stepNumber) => {
                onStepClick(stepNumber);
              }}
            />
          </Col>
        )}
        {((isWaterHeaterSystem && selectedStep !== 2) ||
          (isDirectMode && selectedStep !== 2) ||
          (isSimplifiedMode && selectedStep !== 3) ||
          (isAdvancedMode && selectedStep !== 4)) && (
          <Col className="col-auto">
            <Row className="align-items-center">
              <Col
                className="col-auto bg-background-16 p-2 rounded-2 cursor-pointer"
                onClick={onSave}
              >
                <MaterialIcon icon="save" color="#384F82" />
              </Col>
              <Col>
                <AppButton
                  text={!auth.token ? "Continue" : "Save & Continue"}
                  className="px-4 font-weight-600"
                  onClick={() => {
                    if (selectedStep < 4) {
                      onStepClick(selectedStep + 1);
                    }
                  }}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
      {((isWaterHeaterSystem && selectedStep !== 2) ||
        (isDirectMode && selectedStep !== 2) ||
        (isSimplifiedMode && selectedStep !== 3) ||
        (isAdvancedMode && selectedStep !== 4)) && (
        <Row className="justify-content-center">
          <Col
            className={`col-auto ${
              isSimplifiedMode || isDirectMode || isWaterHeaterSystem
                ? `d-block d-lg-none mt-4 mt-lg-0`
                : `d-block d-xl-none mt-4 mt-xl-0`
            }`}
          >
            <StepsNumber
              steps={steps}
              selectedStep={selectedStep}
              onStepClick={(stepNumber) => {
                onStepClick(stepNumber);
              }}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default StepsHeader;
